import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Card, Typography, Table, Select, Button, Space, AutoComplete, Input,
  message, Tag, Modal, Radio, Empty, Tooltip, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined, CopyOutlined,
  UsergroupAddOutlined, SearchOutlined,
} from '@ant-design/icons';
import { familiarService, eventoService } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title, Text } = Typography;

/**
 * Vinculación masiva de familiares.
 *
 * La vinculación de a uno ya estaba bien resuelta, pero su formulario vive en
 * una pestaña de la ficha del animal: cargar un remate obligaba a entrar y
 * salir de la ficha una vez por bicho. Sobre los datos reales son 205 vínculos
 * en 58 animales — el costo era la NAVEGACIÓN, no el formulario. Esta pantalla
 * la elimina: todos los animales del evento en una tabla, se expande el que se
 * quiere cargar y se guarda ahí mismo.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de módulo
// ─────────────────────────────────────────────────────────────────────────────

/** Espejo de `VINCULO_MAX` en `familiar_routes.py` (columna String(100)).
 *  Cortar acá evita un round trip para que el server rechace con 400 algo que
 *  el cliente ya sabía que no entraba. */
const VINCULO_MAX = 100;

let contadorClaves = 0;
const filaNueva = () => ({
  clave: `nueva-${++contadorClaves}`,
  id_familiar: null,
  vinculo_familiar: '',
});

const filasDesde = (vinculaciones) =>
  (vinculaciones || []).map(v => ({
    clave: `af-${v.id_animal_familiar}`,
    id_familiar: v.id_familiar,
    vinculo_familiar: v.vinculo_familiar || '',
  }));

/** Filas → body del PUT. Las filas sin familiar elegido no viajan. */
const aPayload = (filas) =>
  (filas || [])
    .filter(f => f.id_familiar)
    .map(f => ({
      id_familiar: f.id_familiar,
      vinculo_familiar: (f.vinculo_familiar || '').trim() || null,
    }));

/** Comparación por VALOR, no por identidad: habilita el botón Guardar. */
const hayCambios = (filas, original) =>
  JSON.stringify(aPayload(filas)) !== JSON.stringify(aPayload(original));

/** Sin acentos ni mayúsculas, para buscar «prenada» y encontrar «Preñada».
 *  Mismo criterio que los filtros de la venta transitoria. */
const plano = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** El filtro de la tabla: RP y nombre por SUBSTRING, corral EXACTO (es un
 *  identificador: con substring, «2» traería 2, 12, 20-29…). Una sola caja
 *  para los tres, porque el productor busca «el 2886» sin pensar en qué
 *  columna está. Pedido del usuario (2026-10-26): con 61 animales en Monte
 *  Grande no había forma de llegar a uno sin paginar a ojo. */
const coincide = (animal, texto) => {
  const t = plano(texto);
  if (!t) return true;
  return plano(animal.rp_animal).includes(t)
    || plano(animal.nom_animal).includes(t)
    || (animal.corral != null && String(animal.corral) === t);
};

/**
 * Si lo tipeado coincide con una etiqueta existente salvo por mayúsculas o
 * espacios, se usa la existente.
 *
 * No es cosmético: hoy conviven en la base `MEDIO HERMANO` (24 usos) y
 * `Medio hermano` (1), y eso se lee en el micrositio PÚBLICO, donde dos
 * hermanos muestran la misma relación escrita de dos formas. El combo sugiere
 * las que ya existen, pero nada impide tipear una variante — esto la absorbe
 * sin bloquear al usuario que sí quiere una etiqueta nueva de verdad.
 */
const normalizarEtiqueta = (valor, etiquetas) => {
  const texto = (valor || '').trim();
  if (!texto) return '';
  return etiquetas.find(e => e.toLowerCase() === texto.toLowerCase()) || texto;
};

/** Una fila del editor. A nivel de módulo para que no se remonte en cada tecla. */
const FilaFamiliar = ({ fila, opciones, etiquetas, usados, onCambiar, onQuitar }) => (
  <Space.Compact style={{ display: 'flex', marginBottom: 8 }}>
    <Select
      showSearch
      allowClear
      placeholder="Buscar familiar por RP o nombre…"
      value={fila.id_familiar}
      onChange={(v) => onCambiar(fila.clave, { id_familiar: v ?? null })}
      // El catálogo más grande tiene 112 familiares: filtrar en memoria es
      // instantáneo y evita un request por tecla.
      filterOption={(input, option) =>
        (option?.busqueda || '').toLowerCase().includes(input.toLowerCase())
      }
      options={opciones.map(o => ({
        ...o,
        // Un familiar ya usado en ESTE animal no se puede repetir: lo prohíbe
        // el UNIQUE (id_animal, id_familiar). Se deshabilita en vez de dejar
        // que el guardado falle.
        disabled: o.value !== fila.id_familiar && usados.has(o.value),
      }))}
      style={{ flex: '1 1 55%' }}
    />
    <AutoComplete
      value={fila.vinculo_familiar}
      onChange={(v) => onCambiar(fila.clave, { vinculo_familiar: v })}
      onBlur={() => onCambiar(fila.clave, {
        vinculo_familiar: normalizarEtiqueta(fila.vinculo_familiar, etiquetas),
      })}
      options={etiquetas.map(e => ({ value: e }))}
      filterOption={(input, option) =>
        (option?.value || '').toLowerCase().includes((input || '').toLowerCase())
      }
      style={{ flex: '1 1 45%' }}
    >
      {/* El input va como hijo y no por props: `maxLength` en el AutoComplete
          no llega al <input> de adentro. */}
      <Input placeholder="Vínculo (madre, media hermana…)" maxLength={VINCULO_MAX} />
    </AutoComplete>
    <Button danger icon={<DeleteOutlined />} onClick={() => onQuitar(fila.clave)} />
  </Space.Compact>
);

/** Editor de un animal: se monta al expandir su fila en la tabla. */
const EditorAnimal = ({
  animal, filas, opciones, etiquetas, guardando, sucio,
  onCambiarFila, onQuitarFila, onAgregarFila, onGuardar, onCopiar,
}) => {
  const usados = useMemo(
    () => new Set(filas.filter(f => f.id_familiar).map(f => f.id_familiar)),
    [filas],
  );
  const sinFamiliarElegido = filas.some(f => !f.id_familiar);
  const sinFamiliares = aPayload(filas).length === 0;

  return (
    <div style={{ padding: '8px 16px 16px' }}>
      {filas.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Este animal todavía no tiene familiares"
          style={{ margin: '8px 0 16px' }}
        />
      ) : filas.map(fila => (
        <FilaFamiliar
          key={fila.clave}
          fila={fila}
          opciones={opciones}
          etiquetas={etiquetas}
          usados={usados}
          onCambiar={onCambiarFila}
          onQuitar={onQuitarFila}
        />
      ))}

      <Space wrap style={{ marginTop: 4 }}>
        <Button icon={<PlusOutlined />} onClick={onAgregarFila}>
          Agregar familiar
        </Button>
        {/* El <span> no es decorativo. Un <button disabled> nativo no dispara
            mouseenter, y antd 5 —a diferencia de v4— ya no envuelve solo los
            botones deshabilitados. Sin esto, el texto que explica POR QUÉ el
            botón está gris es justo el único que no se puede leer. */}
        <Tooltip title={sinFamiliarElegido ? 'Hay una fila sin familiar elegido' : ''}>
          <span style={{ display: 'inline-block' }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={guardando}
              disabled={!sucio || sinFamiliarElegido}
              onClick={onGuardar}
            >
              Guardar
            </Button>
          </span>
        </Tooltip>
        {/* Tres ramas, no dos: son DOS condiciones distintas las que lo
            deshabilitan, y un tooltip que explica sólo una deja al usuario
            mirando un botón gris con un texto que no viene al caso. */}
        <Tooltip
          title={
            sucio ? 'Guardá los cambios antes de copiar'
              : sinFamiliares ? 'Este animal no tiene familiares para copiar'
                : 'Copiar estos familiares a otros animales'
          }
        >
          <span style={{ display: 'inline-block' }}>
            <Button
              icon={<CopyOutlined />}
              disabled={sucio || sinFamiliares}
              onClick={() => onCopiar(animal)}
            >
              Copiar a…
            </Button>
          </span>
        </Tooltip>
        {sucio && <Text type="warning">Sin guardar</Text>}
      </Space>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const VincularFamiliares = () => {
  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [idEvento, setIdEvento] = useState(null);
  const [datos, setDatos] = useState(null);       // { animales, catalogo, etiquetas }
  const [loading, setLoading] = useState(false);
  const [borradores, setBorradores] = useState({});   // id_animal → filas
  const [guardando, setGuardando] = useState({});     // id_animal → bool
  const [expandidas, setExpandidas] = useState([]);
  const [copiaDe, setCopiaDe] = useState(null);       // animal origen | null
  const [copiaDestinos, setCopiaDestinos] = useState([]);
  const [copiaModo, setCopiaModo] = useState('sumar');
  const [copiando, setCopiando] = useState(false);
  // Espejo de `originales` para que `cargar` lo lea sin tomarlo por dependencia.
  const originalesRef = useRef({});
  // Numero de la ultima carga pedida, para descartar respuestas que llegan tarde.
  const peticionRef = useRef(0);

  useEffect(() => {
    const fetchEventos = async () => {
      try {
        const res = await eventoService.getListaCorta();
        setEventos(eventosDeListaCorta(res));
      } catch (error) {
        message.error(error.response?.data?.error || 'Error al cargar eventos');
      } finally {
        setLoadingEventos(false);
      }
    };
    fetchEventos();
  }, []);

  /**
   * Trae los datos del evento.
   *
   * `esRefresco` distingue los DOS trabajos que hace esta función, que tienen
   * reglas opuestas:
   *
   * - **Carga inicial / cambio de remate** (`esRefresco: false`): si falla hay
   *   que limpiar, porque si no la tabla queda mostrando los animales del
   *   evento anterior mientras el selector dice otro.
   * - **Refresco posterior a una copia** (`esRefresco: true`): si falla NO se
   *   limpia nada. La copia ya se hizo en el server y los borradores en curso
   *   son trabajo del usuario; vaciarle la pantalla por un fallo de red al
   *   releer es castigarlo por una operación que salió bien.
   *
   * Y en el refresco tampoco se pisan los animales que el usuario tenga a
   * medio editar: la copia tocó otros animales, no tiene por qué costarle el
   * trabajo en curso.
   *
   * "Sucio" se mide contra lo que el server tenía ANTES de esta recarga
   * (`originalesRef`), NO contra lo que trae ahora. La diferencia no es sutil:
   * al animal que acaba de recibir la copia, lo que trae ahora es justamente
   * distinto de su borrador, así que compararlo contra eso lo daría por
   * editado, descartaría la copia recién hecha y encima la dejaría marcada
   * como "sin guardar" — con lo cual el próximo Guardar la borraba.
   *
   * Va por ref y no por closure porque `cargar` es un useCallback estable: si
   * `originales` entrara por dependencia, se recrearía en cada tecleo.
   */
  const cargar = useCallback(async (id, { esRefresco = false } = {}) => {
    // Las respuestas no vuelven necesariamente en orden. Si se cambia de
    // remate A a B y la de A llega despues, escribirla dejaria la tabla con
    // los animales de A y el selector diciendo B. Gana siempre la ultima
    // pedida, no la ultima en llegar.
    const peticion = ++peticionRef.current;
    const vigente = () => peticion === peticionRef.current;

    setLoading(true);
    try {
      const res = await familiarService.getDatosVinculacionMasiva(id);
      if (!vigente()) return;
      const animales = res.data.animales || [];
      setBorradores(previos => Object.fromEntries(animales.map(a => {
        const delServidor = filasDesde(a.vinculaciones);
        const borrador = previos[a.id_animal];
        const sucio = esRefresco && borrador
          && hayCambios(borrador, originalesRef.current[a.id_animal]);
        return [a.id_animal, sucio ? borrador : delServidor];
      })));
      setDatos(res.data);
      if (!esRefresco) setExpandidas([]);
    } catch (error) {
      // El error de una carga que ya quedó vieja tampoco se muestra: sería un
      // cartel rojo sobre datos que cargaron bien.
      if (!vigente()) return;
      if (esRefresco) {
        // La copia YA se hizo en el server. Vaciar la pantalla acá le borraría
        // al usuario los borradores en curso por un fallo de red al releer, o
        // sea castigarlo por una operación que salió bien. Se queda con lo que
        // tiene en pantalla, que sigue siendo válido salvo los animales que
        // acaba de tocar la copia.
        message.warning('Los familiares se copiaron, pero no se pudo refrescar la pantalla. Recargá para verlos.');
      } else {
        // Carga inicial o cambio de remate: acá SÍ hay que limpiar, porque si
        // no la tabla queda mostrando los animales de A mientras el selector
        // ya dice B.
        setDatos(null);
        setBorradores({});
        message.error(error.response?.data?.error || 'Error al cargar los datos del evento');
      }
    } finally {
      // Sólo la carga vigente apaga el spinner. Si lo apagara la vieja, la
      // pantalla se vería lista mientras la buena todavía viene en camino.
      if (vigente()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (idEvento) cargar(idEvento);
  }, [idEvento, cargar]);

  // Originales por animal, para saber qué está sucio sin re-pedir al server.
  const originales = useMemo(() => Object.fromEntries(
    (datos?.animales || []).map(a => [a.id_animal, filasDesde(a.vinculaciones)]),
  ), [datos]);

  useEffect(() => { originalesRef.current = originales; }, [originales]);

  const opciones = useMemo(() => (datos?.catalogo || []).map(f => ({
    value: f.id_familiar,
    label: f.rp_familiar ? `${f.rp_familiar} — ${f.nom_familiar || 'sin nombre'}` : f.nom_familiar,
    busqueda: `${f.rp_familiar || ''} ${f.nom_familiar || ''}`,
  })), [datos]);

  const etiquetas = datos?.etiquetas || [];

  // Una pasada por cambio, en vez de recalcular por fila en cada render:
  // `hayCambios` serializa, y esto se evalúa con cada tecla que se tipea en
  // cualquier fila expandida.
  const sucios = useMemo(() => Object.fromEntries(
    (datos?.animales || []).map(a => [
      a.id_animal, hayCambios(borradores[a.id_animal], originales[a.id_animal]),
    ]),
  ), [datos, borradores, originales]);

  const mutarFilas = (idAnimal, fn) =>
    setBorradores(prev => ({ ...prev, [idAnimal]: fn(prev[idAnimal] || []) }));

  const cambiarFila = (idAnimal) => (clave, cambios) =>
    mutarFilas(idAnimal, filas => filas.map(f => (f.clave === clave ? { ...f, ...cambios } : f)));

  const quitarFila = (idAnimal) => (clave) =>
    mutarFilas(idAnimal, filas => filas.filter(f => f.clave !== clave));

  const agregarFila = (idAnimal) => () =>
    mutarFilas(idAnimal, filas => [...filas, filaNueva()]);

  const guardar = (animal) => async () => {
    const idAnimal = animal.id_animal;
    setGuardando(prev => ({ ...prev, [idAnimal]: true }));
    try {
      const res = await familiarService.reemplazarVinculaciones(
        idAnimal, aPayload(borradores[idAnimal]),
      );
      const vinculaciones = res.data.vinculaciones || [];
      // Se sincroniza el animal en `datos` (no se recarga todo el evento): el
      // usuario puede tener otros animales a medio editar y una recarga
      // completa le borraría el trabajo.
      setDatos(prev => ({
        ...prev,
        animales: prev.animales.map(a =>
          a.id_animal === idAnimal ? { ...a, vinculaciones } : a),
        // Las etiquetas nuevas entran al combo en el acto. Si esperaran a una
        // recarga, al pasar al animal siguiente `normalizarEtiqueta` no las
        // conocería y tipear la misma con otra caja de mayúsculas generaría
        // justo el duplicado que esa función existe para evitar.
        etiquetas: [...new Set([
          ...(prev.etiquetas || []),
          ...vinculaciones.map(v => v.vinculo_familiar).filter(Boolean),
        ])].sort(),
      }));
      setBorradores(prev => ({ ...prev, [idAnimal]: filasDesde(vinculaciones) }));
      message.success(`Familiares de ${animal.nom_animal} guardados`);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar los familiares');
    } finally {
      setGuardando(prev => ({ ...prev, [idAnimal]: false }));
    }
  };

  const abrirCopia = (animal) => {
    setCopiaDe(animal);
    setCopiaDestinos([]);
    setCopiaModo('sumar');
  };

  const confirmarCopia = async () => {
    setCopiando(true);
    try {
      const res = await familiarService.copiarVinculaciones({
        id_animal_origen: copiaDe.id_animal,
        destinos: copiaDestinos,
        modo: copiaModo,
      });
      message.success(res.data.message);
      setCopiaDe(null);
      // La copia tocó varios animales de una, así que hay que releer del
      // server. Pero sin pisar lo que el usuario tenga a medio editar en otras
      // filas — ver el comentario de `cargar`.
      await cargar(idEvento, { esRefresco: true });
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al copiar los familiares');
    } finally {
      setCopiando(false);
    }
  };

  const columnas = [
    { title: 'Corral', dataIndex: 'corral', width: 90, render: (c) => c ?? '—' },
    { title: 'RP', dataIndex: 'rp_animal', width: 130 },
    { title: 'Animal', dataIndex: 'nom_animal' },
    {
      title: 'Categoría', dataIndex: 'nom_categoria', width: 190,
      render: (c) => c || <Text type="secondary">—</Text>,
    },
    {
      title: 'Familiares', width: 150,
      render: (_, animal) => {
        const cantidad = aPayload(borradores[animal.id_animal]).length;
        const sucio = sucios[animal.id_animal];
        return (
          <Space>
            <Tag color={cantidad ? 'blue' : 'default'}>{cantidad}</Tag>
            {sucio && <Tag color="warning">sin guardar</Tag>}
          </Space>
        );
      },
    },
  ];

  // En useMemo: `datos?.animales || []` creaba un array nuevo por render y el
  // filtro de abajo se recalculaba con cada tecla de cualquier fila expandida.
  const animales = useMemo(() => datos?.animales || [], [datos]);
  const pendientes = animales.filter(a => sucios[a.id_animal]).length;
  const [filtro, setFiltro] = useState('');
  // Filtra la TABLA, no los borradores: lo que está a medio editar en un animal
  // que el filtro esconde sigue vivo, y «pendientes» lo sigue contando.
  const animalesVisibles = useMemo(
    () => (filtro.trim() ? animales.filter(a => coincide(a, filtro)) : animales),
    [animales, filtro],
  );

  /** Cambiar de remate descarta los borradores: con 58 animales cargados de a
   *  uno, hacerlo sin avisar es tirar el trabajo de una tarde. */
  const cambiarEvento = (nuevo) => {
    if (pendientes === 0) {
      setIdEvento(nuevo);
      return;
    }
    Modal.confirm({
      title: 'Tenés cambios sin guardar',
      content: `Hay ${pendientes} animal(es) con familiares sin guardar. Si cambiás de remate se pierden.`,
      okText: 'Cambiar igual',
      okButtonProps: { danger: true },
      cancelText: 'Volver',
      onOk: () => setIdEvento(nuevo),
    });
  };

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <UsergroupAddOutlined /> Vincular familiares
          </Title>
          <Text type="secondary">
            Todos los animales del remate en una sola pantalla. Se expande el animal,
            se le cargan los familiares y se guarda ahí mismo.
          </Text>
        </div>

        {/* Es la puerta de entrada de la pantalla: sin `loading`, mientras
            llegan los eventos el desplegable se ve vacío y da a entender que
            la cabaña no tiene remates cargados. */}
        <Space wrap>
          <Select
            showSearch
            loading={loadingEventos}
            disabled={loadingEventos}
            placeholder={loadingEventos ? 'Cargando remates…' : 'Elegí un remate'}
            value={idEvento}
            onChange={cambiarEvento}
            style={{ minWidth: 340 }}
            optionFilterProp="label"
            options={eventos.map(e => ({ value: e.id_evento, label: e.nombre_evento }))}
          />
          {idEvento && (
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar por RP, nombre o corral"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              style={{ width: 300 }}
            />
          )}
        </Space>

        {pendientes > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`Tenés ${pendientes} animal(es) con cambios sin guardar.`}
            description="Cada animal se guarda por separado, con su propio botón Guardar."
          />
        )}

        {idEvento && (
          <Table
            rowKey="id_animal"
            loading={loading}
            columns={columnas}
            dataSource={animalesVisibles}
            size="small"
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: filtro.trim()
              ? `Ningún animal coincide con «${filtro.trim()}»`
              : 'Este remate no tiene animales' }}
            pagination={{
              pageSize: 25,
              showSizeChanger: true,
              showTotal: (t) => (filtro.trim() && t !== animales.length
                ? `${t} de ${animales.length} animales`
                : `${t} animales`),
            }}
            expandable={{
              expandedRowKeys: expandidas,
              onExpandedRowsChange: (claves) => setExpandidas([...claves]),
              expandedRowRender: (animal) => (
                <EditorAnimal
                  animal={animal}
                  filas={borradores[animal.id_animal] || []}
                  opciones={opciones}
                  etiquetas={etiquetas}
                  guardando={!!guardando[animal.id_animal]}
                  sucio={!!sucios[animal.id_animal]}
                  onCambiarFila={cambiarFila(animal.id_animal)}
                  onQuitarFila={quitarFila(animal.id_animal)}
                  onAgregarFila={agregarFila(animal.id_animal)}
                  onGuardar={guardar(animal)}
                  onCopiar={abrirCopia}
                />
              ),
            }}
          />
        )}
      </Space>

      <Modal
        title={`Copiar los familiares de ${copiaDe?.nom_animal || ''}`}
        open={!!copiaDe}
        onCancel={() => setCopiaDe(null)}
        onOk={confirmarCopia}
        okText="Copiar"
        okButtonProps={{ disabled: copiaDestinos.length === 0, loading: copiando }}
        width={620}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Los hermanos comparten familia: el 70% de los vínculos cargados vienen de
            familiares que se repiten entre animales. Elegí a cuáles replicarlos.
          </Text>

          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="Animales de destino"
            value={copiaDestinos}
            onChange={setCopiaDestinos}
            optionFilterProp="label"
            style={{ width: '100%' }}
            maxTagCount="responsive"
            options={animales
              .filter(a => a.id_animal !== copiaDe?.id_animal)
              .map(a => ({
                value: a.id_animal,
                label: `${a.rp_animal || 's/RP'} — ${a.nom_animal}${a.corral ? ` (corral ${a.corral})` : ''}`,
              }))}
          />

          <Radio.Group value={copiaModo} onChange={(e) => setCopiaModo(e.target.value)}>
            <Space direction="vertical">
              <Radio value="sumar">
                <b>Sumar</b> — agrega los que falten y respeta lo que el animal ya tenía.
                Repetirlo no duplica nada.
              </Radio>
              <Radio value="reemplazar">
                <b>Reemplazar</b> — deja el destino con exactamente estos familiares.
                Lo que tuviera antes se pierde.
              </Radio>
            </Space>
          </Radio.Group>
        </Space>
      </Modal>
    </Card>
  );
};

export default VincularFamiliares;
